import {Boost, PremiumBoostsStatus, PrepaidGiveaway} from '@layer';
import {LangPackKey, i18n, joinElementsWith} from '@lib/langPack';
import Section from '@components/section';
import {SliderSuperTabEventable} from '@components/sliderTab';
import {Accessor, createMemo, createRoot, createSignal, For, JSX, onCleanup, Show} from 'solid-js';
import {render} from 'solid-js/web';
import RowTsx from '@components/rowTsx';
import {avatarNew, AvatarNew} from '@components/avatarNew';
import LimitLine from '@components/limit';
import {LoadableList, StatisticsOverviewItems, createLoadableList, MoreButton, makeAbsStats} from '@components/sidebarRight/tabs/statistics';
import showBoostsViaGiftsPopup, {BoostsBadge} from '@components/popups/boostsViaGifts';
import Button from '@components/button';
import {attachClickEvent} from '@helpers/dom/clickEvent';
import {InviteLink} from '@components/sidebarLeft/tabs/inviteLink';
import {horizontalMenu} from '@components/horizontalMenu';
import classNames from '@helpers/string/classNames';
import {formatFullSentTime} from '@helpers/date';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import Icon from '@components/icon';
import toggleDisability from '@helpers/dom/toggleDisability';
import findUpClassName from '@helpers/dom/findUpClassName';
import rootScope from '@lib/rootScope';
import showGiftLinkPopup from '@components/popups/giftLink';
import {toastNew} from '@components/toast';
import indexOfAndSplice from '@helpers/array/indexOfAndSplice';
import appImManager from '@lib/appImManager';
import {createPaymentPopup} from '@components/popups/payment';
import formatStarsAmount from '@appManagers/utils/payments/formatStarsAmount';
import showBoostPopup from '@components/popups/boost';
import Tabs from '@components/tabs';
import {wrapSolidComponent} from '@helpers/solid/wrapSolidComponent';

const getColorByMonths = (months: number) => {
  return months === 12 ? 'red' : (months === 3 ? 'green' : 'blue');
};

const getBoostsDays = (from: number, to: number) => Math.round((to - from) / 86400);
const getBoostMonths = (from: number, to: number) => Math.round(getBoostsDays(from, to) / 30);

export const CPrepaidGiveaway = (props: {
  giveaway: PrepaidGiveaway,
  appConfig: MTAppConfig,
  clickable?: true | (() => void)
}) => {
  const {quantity} = props.giveaway;
  const stars = (props.giveaway as PrepaidGiveaway.prepaidStarsGiveaway).stars;
  const months = (props.giveaway as PrepaidGiveaway.prepaidGiveaway).months;
  const boosts = stars ? (props.giveaway as PrepaidGiveaway.prepaidStarsGiveaway).boosts : (props.appConfig.giveaway_boosts_per_premium || 1) * quantity;
  const avatar = AvatarNew({size: 42});
  if(stars) {
    avatar.set({icon: 'star', color: 'stars'});
  } else {
    avatar.set({icon: 'gift_premium_filled', color: getColorByMonths(months)});
  }
  return (
    <RowTsx clickable={props.clickable}>
      <RowTsx.Title class="text-bold">
        {i18n(stars ? 'Stars' : 'BoostingGiveawayMsgInfoPlural1', [stars || quantity])}
      </RowTsx.Title>
      <RowTsx.Subtitle>
        {i18n(stars ? 'Giveaway.Prepaid.For' : 'Giveaway.Prepaid.Subtitle', [
          quantity,
          i18n('Giveaway.Prepaid.Period', [months])
        ])}
      </RowTsx.Subtitle>
      <RowTsx.RightContent><BoostsBadge boosts={boosts} /></RowTsx.RightContent>
      <RowTsx.Media size="abitbigger">{avatar.node}</RowTsx.Media>
    </RowTsx>
  );
};

export default class AppBoostsTab extends SliderSuperTabEventable {
  public peerId: PeerId;
  private isBroadcast: boolean;
  private targets: Map<HTMLElement, Boost>;
  private canCreateGiveaway: boolean;

  private _construct(
    boostsStatus: PremiumBoostsStatus,
    appConfig: MTAppConfig,
    boostsList: Accessor<LoadableList>,
    giftsBoostsList: Accessor<LoadableList>
  ) {
    const limitLine = new LimitLine({
      progress: true,
      hint: {
        icon: 'boost_filled',
        noStartEnd: true
      }
    });

    const isMaxLevel = boostsStatus.next_level_boosts === undefined;
    const progress = isMaxLevel ?
      1 :
      (boostsStatus.boosts - boostsStatus.current_level_boosts) / (boostsStatus.next_level_boosts - boostsStatus.current_level_boosts);

    limitLine.setProgress(
      progress,
      '' + boostsStatus.boosts,
      {
        from1: i18n('BoostsLevel', [boostsStatus.level]),
        to1: i18n('BoostsLevel', [boostsStatus.level + 1]),
        from2: i18n('BoostsLevel', [boostsStatus.level]),
        to2: i18n('BoostsLevel', [boostsStatus.level + 1])
      }
    );

    limitLine._setHintActive();

    const url = boostsStatus.boost_url;

    const inviteLink = new InviteLink({
      listenerSetter: this.listenerSetter,
      url
    });

    const boostsViaGiftsButton = Button('btn-primary btn-transparent primary', {icon: 'gift_premium_filled', text: 'BoostingGetBoostsViaGifts'});
    attachClickEvent(boostsViaGiftsButton, () => {
      showBoostsViaGiftsPopup(this.peerId);
    }, {listenerSetter: this.listenerSetter});

    const boostButton = Button('btn-primary btn-transparent primary', {icon: 'addboost', text: this.isBroadcast ? 'BoostChannel' : 'BoostGroup'});
    attachClickEvent(boostButton, () => {
      showBoostPopup(this.peerId);
    }, {listenerSetter: this.listenerSetter});

    const noBoostersHint = i18n('NoBoostersHint');
    noBoostersHint.classList.add('boosts-no-boosters');

    let tabs: HTMLDivElement, content: HTMLDivElement;

    const MenuTab = (props: {
      key: LangPackKey,
      count: number
    }) => {
      return (
        <Tabs.MenuTab class="boosts-users-tab">{i18n(props.key, [props.count])}</Tabs.MenuTab>
      );
    };

    const ContentTab = (props: {
      list: LoadableList,
      hide: boolean,
      moreKey: LangPackKey
    }) => {
      return (
        <Tabs.ContentTab
          class={classNames('boosts-users-content', !props.list.count && 'is-empty')}
          hide={props.hide}
        >
          <Show when={props.list.count} fallback={noBoostersHint}>
            {props.list.rendered}
            <Show when={!!props.list.loadMore}>
              <MoreButton
                count={props.list.count - props.list.rendered.length}
                callback={() => props.list.loadMore()}
                key={props.moreKey}
              />
            </Show>
          </Show>
        </Tabs.ContentTab>
      );
    };

    const [tab, setTab] = createSignal(0);
    const [prepaidGiveaways, setPrepaidGiveaways] = createSignal(boostsStatus.prepaid_giveaways?.slice() || [], {equals: false});
    const onlyGiftedBoosts = createMemo(() => boostsStatus.gift_boosts === boostsStatus.boosts);
    const showGifts = createMemo(() => !onlyGiftedBoosts() && !!giftsBoostsList().count);

    const ret = (
      <>
        <Section>
          {limitLine.container}
          <StatisticsOverviewItems items={[{
            title: 'BoostsLevel2',
            value: makeAbsStats(boostsStatus.level),
            includeZeroValue: true
          }, {
            title: 'PremiumSubscribers',
            value: boostsStatus.premium_audience,
            includeZeroValue: true,
            describePercentage: true
          }, {
            title: 'BoostsExisting',
            value: makeAbsStats(boostsStatus.boosts),
            includeZeroValue: true
          }, {
            title: 'BoostsToLevel',
            value: makeAbsStats(boostsStatus.next_level_boosts - boostsStatus.boosts)
          }]} />
        </Section>
        {this.canCreateGiveaway && prepaidGiveaways().length && (
          <Section name="Giveaway.Prepaid" nameArgs={[1]} caption="BoostingSelectPaidGiveaway">
            <For each={prepaidGiveaways()}>{(prepaidGiveaway) => {
              return (
                <CPrepaidGiveaway
                  giveaway={prepaidGiveaway}
                  appConfig={appConfig}
                  clickable={() => {
                    showBoostsViaGiftsPopup(
                      this.peerId,
                      prepaidGiveaway,
                      () => {
                        setPrepaidGiveaways((giveaways) => {
                          indexOfAndSplice(giveaways, prepaidGiveaway);
                          return giveaways;
                        });
                      }
                    );
                  }}
                />
              );
            }}</For>
          </Section>
        )}
        <Tabs.MenuShell betweenSections>
          <Tabs.Menu ref={tabs} class="boosts-users-tabs">
            <MenuTab key="BoostingBoostsCount" count={boostsList().count} />
            {showGifts() && <MenuTab key="BoostingGiftsCount" count={giftsBoostsList().count} />}
          </Tabs.Menu>
        </Tabs.MenuShell>
        <Section class="boosts-users-container">
          <Tabs.Content ref={content} class="boosts-users-contents" onClick={async(e) => {
            const target = findUpClassName(e.target, 'row');
            const boost = this.targets.get(target);
            if(!boost) {
              return;
            }

            if(boost.stars) {
              createPaymentPopup({
                noPaymentForm: true,
                transaction: {
                  _: 'starsTransaction',
                  date: boost.date,
                  id: '',
                  peer: {
                    _: 'starsTransactionPeer',
                    peer: {
                      _: 'peerChannel',
                      channel_id: this.peerId.toChatId()
                    }
                  },
                  pFlags: {},
                  amount: formatStarsAmount(boost.stars),
                  giveaway_post_id: boost.giveaway_msg_id
                },
                boost
              });
              return;
            }

            const slug = boost.used_gift_slug;
            const peerId = boost.user_id?.toPeerId(false);
            if(peerId && !boost.pFlags.gift && !boost.pFlags.unclaimed && !boost.pFlags.giveaway) {
              appImManager.setInnerPeer({peerId: boost.user_id.toPeerId(false)});
            } else if(peerId && peerId !== rootScope.myId) {
              showGiftLinkPopup(
                slug,
                undefined,
                {
                  _: 'payments.checkedGiftCode',
                  chats: [],
                  date: boost.date,
                  days: getBoostsDays(boost.date, boost.expires),
                  pFlags: {via_giveaway: boost.pFlags.giveaway || undefined},
                  users: [],
                  from_id: await this.managers.appPeersManager.getOutputPeer(this.peerId),
                  giveaway_msg_id: boost.giveaway_msg_id,
                  slug,
                  to_id: peerId.toUserId(),
                  used_date: slug ? 1 : undefined
                }
              );
            } else if(slug) {
              showGiftLinkPopup(slug);
            } else {
              toastNew({langPackKey: 'BoostingRecipientWillBeSelected'});
            }
          }}>
            <ContentTab list={boostsList()} hide={tab() !== 0} moreKey="BoostingShowMoreBoosts" />
            {showGifts() && <ContentTab list={giftsBoostsList()} hide={tab() !== 1} moreKey="BoostingShowMoreGifts" />}
          </Tabs.Content>
        </Section>
        <Section name="LinkForBoosting" caption="BoostingShareThisLink">
          {inviteLink.container}
        </Section>
        <Section caption={this.canCreateGiveaway ? 'BoostingGetMoreBoosts' : undefined}>
          {boostButton}
          {this.canCreateGiveaway && boostsViaGiftsButton}
        </Section>
      </>
    );

    const selectTab = horizontalMenu(tabs, content, (index) => {
      setTab(index);
    }, undefined, undefined, undefined, this.listenerSetter);
    selectTab(tab());

    return ret;
  }

  private renderBoost = async(boost: Boost) => {
    const boosts = 1 * (boost.multiplier || 1);
    const months = getBoostMonths(boost.date, boost.expires);
    let peerId = boost.user_id?.toPeerId(false);
    if(peerId === rootScope.myId && boost.pFlags.unclaimed) {
      peerId = undefined;
    }

    let badge: HTMLElement;
    if(boosts > 1) {
      badge = document.createElement('span');
      badge.classList.add('boosts-user-boosts', 'boosts-user-badge');
      badge.append(Icon('boost_filled'), ` ${boosts}`);
    }

    let title: HTMLElement;
    if(peerId) {
      title = await wrapPeerTitle({peerId});
      title.classList.add('boosts-user-name');
    } else if(boost.stars) {
      title = i18n('Stars', [boost.stars]);
    } else {
      title = i18n(boost.pFlags.unclaimed ? 'BoostingUnclaimed' : 'BoostingToBeDistributed');
    }

    let subtitle: HTMLElement;
    if(peerId || boost.stars) {
      subtitle = i18n('BoostsExpiration', [boosts, formatFullSentTime(boost.expires, undefined, true)]);
    } else {
      subtitle = document.createElement('span');
      subtitle.append(
        ...joinElementsWith([
          i18n('BoostingShortMonths', [months]),
          formatFullSentTime(boost.expires, undefined, true)
        ], ' • ')
      );
    }

    let rightContent: HTMLElement;
    if(boost.pFlags.giveaway || boost.pFlags.gift) {
      rightContent = document.createElement('span');
      rightContent.classList.add('boosts-user-badge-right', 'boosts-user-badge');
      rightContent.append(
        Icon(boost.pFlags.giveaway ? 'gift_premium_filled' : 'gift'),
        i18n(boost.pFlags.giveaway ? 'BoostingGiveaway' : 'BoostingGift')
      );

      rightContent.classList.toggle('is-gift', !boost.pFlags.giveaway && !!boost.pFlags.gift);
    }

    const middleware = this.middlewareHelper.get();
    const avatar = avatarNew({
      peerId,
      size: 42,
      middleware
    });
    const container = wrapSolidComponent(() => (
      <RowTsx clickable noWrap>
        <RowTsx.Title class="boosts-user-title">
          {title}
          {badge}
        </RowTsx.Title>
        <RowTsx.Subtitle>{subtitle}</RowTsx.Subtitle>
        <Show when={rightContent}>
          <RowTsx.RightContent element={rightContent} />
        </Show>
        <RowTsx.Media size="abitbigger">{avatar.node}</RowTsx.Media>
      </RowTsx>
    ), middleware);

    if(peerId) {
      container.dataset.peerId = '' + peerId;
    }

    if(peerId) {
      await avatar.readyThumbPromise;
    } else if(boost.stars) {
      avatar.set({
        icon: 'star',
        color: 'stars'
      });
    } else {
      avatar.set({
        icon: boost.pFlags.unclaimed ? 'deleteuser' : 'noncontacts',
        color: getColorByMonths(months)
      });
    }

    this.targets.set(container, boost);
    return container;
  };

  public async init(peerId: PeerId) {
    this.container.classList.add('boosts-container');

    this.peerId = peerId;
    this.isBroadcast = await this.managers.appPeersManager.isBroadcast(peerId);
    this.targets = new Map();

    this.setTitle('Boosts');

    const createLoader = (gifts?: boolean) => {
      const middleware = this.middlewareHelper.get();
      let offset = '', isFirst = true;
      const loadMore = async() => {
        const limit = isFirst ? 20 : 100;
        isFirst = false;
        const boostsList = await this.managers.appBoostsManager.getBoostsList({peerId, offset, limit, gifts});
        if(!middleware()) return;

        const promises = boostsList.boosts.map(this.renderBoost);
        const rendered = await Promise.all(promises);

        setF((value) => {
          value.count = boostsList.count;
          offset = boostsList.next_offset;
          if(!offset) {
            value.loadMore = undefined;
          }

          value.rendered.push(...rendered);
          return value;
        });
      };

      const [f, setF] = createLoadableList({loadMore});
      return f;
    };

    const [boostsList, giftsBoostsList] = createRoot((dispose) => {
      const middleware = this.middlewareHelper.get();
      middleware.onDestroy(dispose);
      return [createLoader(false), createLoader(true)]
    });

    const [boostsStatus, appConfig, _, __, canCreateGiveaway] = await Promise.all([
      this.managers.appBoostsManager.getBoostsStatus(peerId),
      this.managers.apiManager.getAppConfig(),
      boostsList().loadMore(),
      giftsBoostsList().loadMore(),
      this.managers.appChatsManager.hasRights(peerId.toChatId(), 'create_giveaway')
    ]);

    this.canCreateGiveaway = canCreateGiveaway;

    const div = document.createElement('div');
    this.scrollable.append(div);
    const dispose = render(() => this._construct(boostsStatus, appConfig, boostsList, giftsBoostsList), div);
    this.eventListener.addEventListener('destroy', dispose);
  }
}
